import { HolidayDto } from '../models/holiday.models';

/** A run of consecutive calendar days, inclusive. `count` is the business days it holds. */
export interface DayRun {
  start: string;
  end: string;
  count: number;
}

/** Formats a local calendar date as yyyy-MM-dd. Invalid or missing dates yield ''. */
export function toIsoDateKey(date: Date | null | undefined): string {
  if (!date || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parses a yyyy-MM-dd key as a local calendar date. An ISO date-time is accepted too; only its
 * date part is read, so a midnight UTC timestamp does not slide into the previous local day.
 */
export function parseIsoDateKey(value: string | null | undefined): Date | null {
  if (!value) return null;
  const iso = value.length > 10 ? value.slice(0, 10) : value;
  const d = new Date(iso + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

/**
 * "Mon, Mar 2, 2026" from a yyyy-MM-dd key: the time off dialogs' date format, shared so the
 * event dialog's From and To dates and the carry-over dates can never drift apart. Built from
 * the key's parts, so it never slides into the previous day in a timezone west of UTC.
 */
export function formatWeekdayDate(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * "Mar 2, 2026" from a yyyy-MM-dd key (an ISO date-time is cut to its date part): the Manage
 * Time Off Request dialog's date format, shared so its own range line and the overlap callout
 * can never drift apart. A key that will not parse comes back as it was, so a bad value is seen
 * rather than hidden, unless the caller passes the `fallback` it wants shown instead.
 */
export function formatShortDate(
  dateKey: string | null | undefined,
  fallback?: string,
): string {
  const d = parseIsoDateKey(dateKey);
  if (!d) return fallback ?? dateKey ?? '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * "Mar 2, 2026 - Mar 4, 2026" from two yyyy-MM-dd keys, or the one date when both fall on the
 * same day. A side that is missing or will not parse drops out, so one good date still shows on
 * its own and two bad ones give ''. Shared by every short range the time off dialogs print.
 */
export function formatShortDateRange(
  startKey: string | null | undefined,
  endKey: string | null | undefined,
): string {
  const start = formatShortDate(startKey, '');
  const end = formatShortDate(endKey, '');
  if (start && end) {
    return start === end ? start : `${start} - ${end}`;
  }
  return start || end;
}

/** Converts the API's MM/dd/yyyy holiday dates into yyyy-MM-dd keys. Malformed entries are skipped. */
export function holidayDateKeys(holidays: HolidayDto[]): Set<string> {
  const keys = new Set<string>();
  for (const holiday of holidays) {
    const [month, day, year] = (holiday?.date || '').split('/');
    if (!month || !day || !year) continue;
    keys.add(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  }
  return keys;
}

export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/** True for a Monday-to-Friday date that is not a holiday: the days the API counts as time off. */
export function isBusinessDay(date: Date, holidays: Set<string>): boolean {
  return !isWeekend(date) && !holidays.has(toIsoDateKey(date));
}

/** The first day after `key` that is not a holiday, as a key. '' when `key` does not parse. */
export function nextNonHolidayDayKey(key: string, holidays: Set<string>): string {
  const d = parseIsoDateKey(key);
  if (!d) return '';
  // Bounded so a pathological holiday set can never spin forever.
  for (let step = 0; step < 366; step++) {
    d.setDate(d.getDate() + 1);
    const next = toIsoDateKey(d);
    if (!holidays.has(next)) return next;
  }
  return '';
}

/** How many of `days` are business days: the figure the API reports as days off. */
export function countBusinessDays(
  days: Iterable<string>,
  holidays: Set<string>,
): number {
  let count = 0;
  for (const key of new Set(days)) {
    const d = parseIsoDateKey(key);
    if (d && isBusinessDay(d, holidays)) count += 1;
  }
  return count;
}

/**
 * Groups day keys into runs of consecutive calendar days. A request is a date range, so a
 * missing weekend day breaks a run just as a missing weekday does. Holidays never break one:
 * the picker never offers them and the API stores them as filler. `count` is the business days
 * in the run, matching how the API counts days off. This mirrors how the server is expected to
 * split an edited request: one request per run.
 */
export function groupContiguousDays(
  days: Iterable<string>,
  holidays: Set<string>,
): DayRun[] {
  const sorted = [...new Set(days)].filter(Boolean).sort();
  const runs: DayRun[] = [];
  for (const key of sorted) {
    const d = parseIsoDateKey(key);
    const countable = d && isBusinessDay(d, holidays) ? 1 : 0;
    const current = runs[runs.length - 1];
    if (current && nextNonHolidayDayKey(current.end, holidays) === key) {
      current.end = key;
      current.count += countable;
    } else {
      runs.push({ start: key, end: key, count: countable });
    }
  }
  return runs;
}
